import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import ShareButtons from '../components/ShareButtons';
import { fatwaNum, trunc, stripHtml, SCHOLAR_COLORS, scholarInitial, fatwaScholarName, FATWA_API, cachedFetch } from '../utils/api';
import { useState, useEffect } from 'react';

const LANG_LABELS = {
  ar: { label: 'العربية', flag: '🇸🇦' },
  en: { label: 'English', flag: '🇬🇧' },
  ur: { label: 'اردو',    flag: '🇵🇰' },
};

// Returns the localised name of a category object based on current lang
function catName(c, lang) {
  if (!c) return '';
  if (lang === 'ar') return c.name_ar || '';
  if (c.translations && c.translations[lang] && c.translations[lang].name) {
    return c.translations[lang].name;
  }
  if (lang === 'en') return c.name_en || c.name_ar || '';
  return c.name_ar || '';
}

// Given a fatwa and the categories list, return the localised category label
function fatwaCatName(f, categories, lang, fallback = '') {
  if (!f) return fallback;
  const cat = (categories || []).find(c =>
    c.id === f.category_id ||
    c.name_ar === f.category_name ||
    c.name_en === f.category_name
  );
  if (cat) return catName(cat, lang);
  return f.category_name || fallback;
}

// Extract the numeric part from a fatwa code like ف101, ع5, etc.
function extractCodeNumber(fatwa) {
  if (fatwa.code) {
    const m = fatwa.code.match(/([فعتخق])(\d+)/);
    if (m) return { prefix: m[1], num: parseInt(m[2], 10) };
  }
  return null;
}

export default function SingleFatwaPage({ fatwa, scholars, categories, prevFatwa: nextFatwa, nextFatwa: prevFatwa }) {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [latestFatwas, setLatestFatwas] = useState([]);
  const [loadingLatest, setLoadingLatest] = useState(false);

  // Pick the correct language version from versions map, fall back to Arabic
  const versions = fatwa ? fatwa.versions || {} : {};
  const hasCurrentLang = !!(versions[lang] && versions[lang].question);
  const v        = hasCurrentLang ? versions[lang] : (versions['ar'] || {});
  const question  = v.question  || (fatwa && fatwa.question)  || '';
  const answer    = v.answer    || (fatwa && fatwa.answer)    || '';
  const full_text = v.full_text || (fatwa && fatwa.full_text) || '';
  const showNoTranslation = lang !== 'ar' && !hasCurrentLang;

  // Languages that have a real translation (not the current one)
  const availableLangs = ['ar', 'en', 'ur'].filter(
    l => l !== lang && versions[l] && versions[l].question
  );

  // Fetch the latest fatwas (excluding the current one)
  useEffect(() => {
    if (!fatwa) return;
    setLoadingLatest(true);
    setLatestFatwas([]);
    cachedFetch(`${FATWA_API}?page=1&per_page=6&sort=newest`)
      .then(json => {
        const list = Array.isArray(json) ? json : (json.data || []);
        setLatestFatwas(list.filter(f => String(f.id) !== String(fatwa.id)).slice(0, 5));
        setLoadingLatest(false);
      })
      .catch(() => setLoadingLatest(false));
  }, [fatwa && fatwa.id]);

  if (!fatwa) return null;

  const num    = fatwaNum(fatwa);
  const scholar = scholars.find(s => s.id === fatwa.researcher_id) || scholars.find(s => s.name === fatwa.scholar);
  const idx    = scholar ? scholars.indexOf(scholar) : 0;
  const bg     = SCHOLAR_COLORS[idx % SCHOLAR_COLORS.length];

  return (
    <div id="page-fatwa-single">
      <div className="pw">
        <div style={{padding:'.8rem 0'}}>
          <a href="/fatwas" onClick={e=>{e.preventDefault();navigate('/fatwas');}}
            style={{color:'var(--g1)',fontWeight:700,fontSize:'var(--t-sm)'}}>
            {t('sf.back')}
          </a>
        </div>

        <div className="single-grid">
          <div className="single-body">
            <div className="single-header">
              <div className="geo-sm"/>
              <div className="s-tag">
                {num} · <span style={{cursor:'pointer',textDecoration:'underline'}}
                  onClick={()=>navigate(`/fatwas?cat=${(categories.find(c=>c.name_ar===fatwa.category_name||c.id===fatwa.category_id)||{}).id||''}`)}>
                  {fatwaCatName(fatwa, categories, lang, t('label.general'))}
                </span>
              </div>

              <h1 style={showNoTranslation ? {color:'var(--ink3)',fontStyle:'italic',opacity:.5} : {}}>
                {showNoTranslation ? t('sf.no.translation.title') : stripHtml(question)}
              </h1>

              <div className="s-meta">
                <span style={{cursor:'pointer'}} onClick={()=>scholar&&navigate(`/scholars/${scholar.id}`)}>
                  👤 {fatwaScholarName(fatwa, scholars, lang, t('label.scholar'))}
                </span>
                <span>📅 {(fatwa.created_at||'').slice(0,10)}</span>
                <span>🗂️ {fatwaCatName(fatwa, categories, lang, '')}</span>
              </div>

              {/* ── Language switcher buttons ── */}
              {availableLangs.length > 0 && (
                <div style={{display:'flex',alignItems:'center',gap:'.6rem',marginTop:'1.1rem',flexWrap:'wrap'}}>
                  <span style={{fontSize:'var(--t-sm)',color:'var(--ink3)',fontWeight:600}}>
                    🌐 {lang==='ar' ? 'اقرأ بلغة أخرى:' : lang==='ur' ? 'دوسری زبان میں پڑھیں:' : 'Read in:'}
                  </span>
                  {availableLangs.map(tl => (
                    <button
                      key={tl}
                      onClick={() => setLang(tl)}
                      style={{
                        display:'inline-flex', alignItems:'center', gap:'.35rem',
                        padding:'.4rem 1rem', borderRadius:'2rem',
                        border:'2px solid var(--g1)', background:'transparent',
                        color:'var(--g1)', fontWeight:700, fontSize:'var(--t-sm)',
                        cursor:'pointer', transition:'all .15s',
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.background='var(--g1)';e.currentTarget.style.color='#fff';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='var(--g1)';}}
                    >
                      {LANG_LABELS[tl].flag} {LANG_LABELS[tl].label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="single-content">
              {(fatwa.tags||[]).length>0&&(
                <div style={{display:'flex',flexWrap:'wrap',gap:'.4rem',marginBottom:'1.2rem'}}>
                  {fatwa.tags.map(tag=><span key={tag} className="ffc-tag">{tag}</span>)}
                </div>
              )}
              {showNoTranslation ? (
                <div style={{textAlign:'center',padding:'3rem 1.5rem',borderRadius:'12px',background:'var(--bg2,#f9f9f9)',border:'1px dashed var(--brd)',margin:'1.5rem 0'}}>
                  <div style={{fontSize:'2.5rem',marginBottom:'1rem'}}>🌐</div>
                  <h3 style={{marginBottom:'.6rem',color:'var(--ink)'}}>{t('sf.no.translation.title')}</h3>
                  <p style={{color:'var(--ink3)',fontSize:'var(--t-sm)',lineHeight:1.7,maxWidth:420,margin:'0 auto 1.5rem'}}>{t('sf.no.translation.body')}</p>
                  <button className="btn-primary" onClick={()=>setLang('ar')}>{t('sf.no.translation.btn')}</button>
                </div>
              ) : (
                <>
                  {answer && (
                    <>
                      <h3>{t('sf.answer')}</h3>
                      <div className="ans-box" dangerouslySetInnerHTML={{__html: answer.startsWith('<') ? answer : '<p>'+answer+'</p>'}}/>
                    </>
                  )}
                  {!answer && full_text && (
                    <>
                      <h3>{t('sf.answer')}</h3>
                      <div className="ans-box" dangerouslySetInnerHTML={{__html: full_text.startsWith('<') ? full_text : '<p>'+full_text+'</p>'}}/>
                    </>
                  )}
                </>
              )}
              <p style={{fontSize:'.8rem',color:'var(--ink4)',marginTop:'2rem',paddingTop:'1rem',borderTop:'1px dashed var(--brd)'}}>
                {t('sf.date.prefix')} {(fatwa.created_at||'').slice(0,10)} · {t('sf.institute')}
              </p>
            </div>

            <ShareButtons title={stripHtml(question)} />
            {(prevFatwa || nextFatwa) && (
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'stretch',gap:'1rem',margin:'2rem 0 0',borderTop:'1px solid var(--brd)',paddingTop:'1.5rem'}}>
                {nextFatwa ? (
                  <button onClick={()=>navigate(`/fatwas/${nextFatwa.id}`)}
                    style={{flex:1,display:'flex',alignItems:'center',gap:'.75rem',padding:'.9rem 1.1rem',background:'var(--surface,#fff)',border:'1px solid var(--brd)',borderRadius:'10px',cursor:'pointer',textAlign:'right',transition:'border-color .2s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='var(--g1)'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='var(--brd)'}>
                    <span style={{fontSize:'1.4rem',color:'var(--g1)',flexShrink:0}}>{lang==='en'?'←':'→'}</span>
                    <span style={{display:'flex',flexDirection:'column',gap:'.2rem',overflow:'hidden',flex:1}}>
                      <span style={{fontSize:'.7rem',color:'var(--g1)',fontWeight:700,letterSpacing:'.05em'}}>{t('asin.next')}</span>
                      <span style={{fontSize:'.85rem',fontWeight:600,color:'var(--ink)',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:1.4}}>{stripHtml((nextFatwa.versions&&nextFatwa.versions[lang]&&nextFatwa.versions[lang].question)||nextFatwa.question||'')}</span>
                    </span>
                  </button>
                ) : <div style={{flex:1}}/>}
                {prevFatwa ? (
                  <button onClick={()=>navigate(`/fatwas/${prevFatwa.id}`)}
                    style={{flex:1,display:'flex',alignItems:'center',justifyContent:'flex-end',gap:'.75rem',padding:'.9rem 1.1rem',background:'var(--surface,#fff)',border:'1px solid var(--brd)',borderRadius:'10px',cursor:'pointer',textAlign:'right',transition:'border-color .2s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='var(--g1)'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='var(--brd)'}>
                    <span style={{display:'flex',flexDirection:'column',gap:'.2rem',overflow:'hidden',flex:1,alignItems:'flex-end'}}>
                      <span style={{fontSize:'.7rem',color:'var(--ink3)',fontWeight:700,letterSpacing:'.05em'}}>{t('asin.prev')}</span>
                      <span style={{fontSize:'.85rem',fontWeight:600,color:'var(--ink)',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:1.4,textAlign:'right'}}>{stripHtml((prevFatwa.versions&&prevFatwa.versions[lang]&&prevFatwa.versions[lang].question)||prevFatwa.question||'')}</span>
                    </span>
                    <span style={{fontSize:'1.4rem',color:'var(--ink3)',flexShrink:0}}>{lang==='en'?'→':'←'}</span>
                  </button>
                ) : <div style={{flex:1}}/>}
              </div>
            )}
          </div>

          <aside>
            {/* Scholar box */}
            <div className="single-sbox">
              <div className="ssb-head">{t('sf.scholar.box')}</div>
              <div style={{padding:'1.2rem',textAlign:'center'}}>
                <div style={{width:64,height:64,margin:'0 auto .8rem',background:bg,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'var(--fam-display)',fontSize:'1.8rem',color:'var(--a4)'}}>
                  {scholarInitial(fatwa.scholar||'م')}
                </div>
                <div style={{fontFamily:'var(--fam-display)',fontSize:'1.05rem',color:'var(--ink)',marginBottom:'.3rem'}}>
                  {fatwaScholarName(fatwa, scholars, lang, t('label.scholar'))}
                </div>
                <button className="scf-btn" style={{width:'100%',marginTop:'.5rem'}}
                  onClick={()=>scholar?navigate(`/scholars/${scholar.id}`):navigate('/scholars')}>
                  {t('sf.scholar.btn')}
                </button>
              </div>
            </div>

            {/* Available translations box */}
            {availableLangs.length > 0 && (
              <div className="single-sbox">
                <div className="ssb-head">
                  🌐 {lang==='ar' ? 'النسخ المتاحة' : lang==='ur' ? 'دستیاب ترجمے' : 'Available Translations'}
                </div>
                <div style={{padding:'1rem',display:'flex',flexDirection:'column',gap:'.5rem'}}>
                  {availableLangs.map(tl => (
                    <button
                      key={tl}
                      onClick={() => setLang(tl)}
                      style={{
                        display:'flex', alignItems:'center', justifyContent:'center', gap:'.5rem',
                        padding:'.6rem', borderRadius:'8px',
                        border:'2px solid var(--g1)', background:'transparent',
                        color:'var(--g1)', fontWeight:700, fontSize:'var(--t-sm)',
                        cursor:'pointer', width:'100%', transition:'all .15s',
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.background='var(--g1)';e.currentTarget.style.color='#fff';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='var(--g1)';}}
                    >
                      {LANG_LABELS[tl].flag} {LANG_LABELS[tl].label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Latest Fatwas box ── */}
            {(() => {
              const visibleLatest = lang === 'ar'
                ? latestFatwas
                : latestFatwas.filter(sf => { const v = (sf.versions||{})[lang]; return !!(v&&v.question); });
              if (!loadingLatest && visibleLatest.length === 0) return null;
              return (
              <div className="single-sbox">
                <div className="ssb-head">
                  📋 {lang==='ar' ? 'أحدث الفتاوى' : lang==='ur' ? 'تازہ ترین فتاویٰ' : 'Latest Fatwas'}
                </div>
                <div style={{padding:'1rem',display:'flex',flexDirection:'column',gap:'.75rem'}}>
                  {loadingLatest && latestFatwas.length === 0 ? (
                    <div style={{
                      display:'flex',alignItems:'center',justifyContent:'center',
                      padding:'.8rem',color:'var(--ink3)',fontSize:'var(--t-sm)',gap:'.5rem'
                    }}>
                      <span style={{
                        display:'inline-block',width:14,height:14,
                        border:'2px solid var(--g1)',borderTopColor:'transparent',
                        borderRadius:'50%',animation:'spin .7s linear infinite'
                      }}/>
                      {lang==='ar' ? 'جارٍ التحميل…' : lang==='ur' ? 'لوڈ ہو رہا ہے…' : 'Loading…'}
                    </div>
                  ) : (
                    visibleLatest.map(sf => {
                      const sfVersions = sf.versions || {};
                      const sfV = sfVersions[lang] || sfVersions['ar'] || {};
                      const sfQ = sfV.question || sf.question || '';
                      const sfNum = fatwaNum(sf);
                      return (
                        <a
                          key={sf.id}
                          href={`/fatwas/${sf.id}`}
                          onClick={e => { e.preventDefault(); navigate(`/fatwas/${sf.id}`); }}
                          style={{
                            display:'block',
                            padding:'.7rem .85rem',
                            borderRadius:'8px',
                            border:'1px solid var(--brd)',
                            background:'var(--surface2, var(--bg2, #f9f9f9))',
                            textDecoration:'none',
                            transition:'border-color .15s, background .15s',
                          }}
                          onMouseEnter={e=>{
                            e.currentTarget.style.borderColor='var(--g1)';
                            e.currentTarget.style.background='var(--g1-subtle, #f0f7f0)';
                          }}
                          onMouseLeave={e=>{
                            e.currentTarget.style.borderColor='var(--brd)';
                            e.currentTarget.style.background='var(--surface2, var(--bg2, #f9f9f9))';
                          }}
                        >
                          <div style={{fontSize:'.7rem',fontWeight:700,color:'var(--g1)',marginBottom:'.25rem',letterSpacing:'.02em'}}>{sfNum}</div>
                          <div style={{
                            fontSize:'var(--t-sm)',
                            color:'var(--ink)',
                            lineHeight:1.45,
                            display:'-webkit-box',
                            WebkitLineClamp:2,
                            WebkitBoxOrient:'vertical',
                            overflow:'hidden',
                          }}>
                            {stripHtml(sfQ) || (lang==='ar' ? '(بدون عنوان)' : lang==='ur' ? '(بغیر عنوان)' : '(No title)')}
                          </div>
                        </a>
                      );
                    })
                  )}
                </div>
              </div>
              );
            })()}

            {/* Tags box */}
            {(fatwa.tags||[]).length>0&&(
              <div className="single-sbox">
                <div className="ssb-head">{t('sf.tags')}</div>
                <div className="cat-pills-wrap">
                  {fatwa.tags.map(tag=>(
                    <a key={tag} href={`/fatwas?q=${tag}`} className="cpill"
                      onClick={e=>{e.preventDefault();navigate(`/fatwas?q=${tag}`);}}>
                      {tag}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>

      {/* Spinner keyframe – injected once */}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}